import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Users, Database, Lightning, ShieldCheck } from "@phosphor-icons/react";
import { useContent } from "@/contexts/ContentContext";

const STAT_ICONS = [Users, Database, Lightning, ShieldCheck];

export default function AboutSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const { content } = useContent();
  const about = content?.about || {};

  const label = about.label || "About Conexus Foundation";
  const titleLines = (about.title || "Pioneering Global\nEnvironmental Awareness").split("\n");
  const p1 = about.paragraph_1 || "";
  const p2 = about.paragraph_2 || "";
  const stats = Array.isArray(about.stats) ? about.stats : [];

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-16 sm:py-24 lg:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="rounded-3xl overflow-hidden aspect-[4/3]">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/c8b3d0a9-5937-480a-8c5c-11fe0e5f5b28/images/79735d6009002c138ca2f68b5cd225d0ca903e119e4ab313a52e30e3cf2efaca.png"
                alt="Research team monitoring Earth data"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-2xl bg-emerald-100 -z-10" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-emerald-700 mb-3 font-body" data-testid="about-label">
              {label}
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-stone-900 mb-6" data-testid="about-title">
              {titleLines.map((l, i) => (
                <span key={i}>
                  {l}
                  {i < titleLines.length - 1 && <br />}
                </span>
              ))}
            </h2>
            {p1 && <p className="font-body text-base text-stone-500 leading-relaxed mb-6" data-testid="about-paragraph-1">{p1}</p>}
            {p2 && <p className="font-body text-base text-stone-500 leading-relaxed mb-10" data-testid="about-paragraph-2">{p2}</p>}

            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat, index) => {
                const Icon = STAT_ICONS[index % STAT_ICONS.length];
                return (
                  <motion.div
                    key={`${stat.label}-${index}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                    className="p-4 rounded-2xl bg-stone-50 border border-stone-100"
                    data-testid={`stat-${(stat.label || "").toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Icon size={20} weight="duotone" className="text-emerald-700 mb-2" />
                    <p className="font-heading text-2xl font-semibold text-stone-900">{stat.value}</p>
                    <p className="font-body text-xs text-stone-500 mt-0.5">{stat.label}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
